/**
 * ANA-R1-09 — cross-window auth synchronisation.
 *
 * Problem: a popped-out widget window (`?popout=1`, opened via `window.open`)
 * runs the full SPA, including its own auth store persisted to
 * `sessionStorage`. `sessionStorage` is per-window-context — the child boots
 * with a COPY of the opener's session but the two never sync afterwards. So
 * once the MAIN window silently refreshes (rotating the access + refresh
 * token), the pop-out keeps the stale tokens; when its access token expires
 * the pop-out's refresh exchange uses an already-rotated refresh token,
 * Zitadel rejects it, the session is cleared and the pop-out is bounced to
 * `/login` — landing the user in a SECOND, parallel session.
 *
 * Fix: a single origin-wide `BroadcastChannel("aero-auth-sync")` over which
 * the refresh AUTHORITY (the main window) publishes every token rotation and
 * every session-end. Followers (pop-outs) apply rotations to their own store
 * and surface session-end inline instead of starting an independent login.
 * Combined with `useProactiveTokenRefresh` no longer running in a pop-out
 * (see proactiveRefresh.ts), exactly one window ever rotates the shared
 * refresh token, so the windows can never diverge.
 *
 * Loop safety: a received token is applied with `applyingFromBroadcast` set,
 * so the store subscription that would normally re-broadcast a rotation
 * stays quiet. Messages also carry the sender's `accessTokenExpiresAt`, and a
 * receiver ignores any rotation that is not newer than what it already holds
 * (idempotent + out-of-order safe).
 */

import { useAuthStore } from "./store.js";
import { isPopoutWindow } from "./windowRole.js";

const CHANNEL_NAME = "aero-auth-sync";

/** A token rotation produced by the refresh authority. */
interface TokensMessage {
  type: "tokens";
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: number;
}

/** The authority's session ended (cleared) or soft-expired (idle). */
interface SessionMessage {
  type: "session";
  status: "cleared" | "expired";
}

type AuthBroadcastMessage = TokensMessage | SessionMessage;

/** Test seam — defaults to globalThis.BroadcastChannel. */
export interface CreateAuthChannelArgs {
  channelFactory?: (name: string) => BroadcastChannel | null;
}

function defaultFactory(name: string): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    return new BroadcastChannel(name);
  } catch {
    return null;
  }
}

// Guards re-broadcast of a rotation we just received from a peer.
let applyingFromBroadcast = false;

/**
 * Apply a peer's rotated tokens to this window's store WITHOUT re-broadcasting.
 * Exported for unit tests. Ignores stale/older rotations (idempotent) and
 * no-ops when there is no session to update (the bare token half can't stand
 * alone — `setAccessToken` already no-ops without a session).
 */
export function applyBroadcastTokens(msg: TokensMessage): void {
  const current = useAuthStore.getState().session;
  if (!current) return;
  // Out-of-order / duplicate guard: only move the deadline forward.
  const have = current.accessTokenExpiresAt ?? 0;
  if (
    msg.accessTokenExpiresAt <= have &&
    current.accessToken === msg.accessToken
  ) {
    return;
  }
  applyingFromBroadcast = true;
  try {
    useAuthStore.getState().setAccessToken({
      accessToken: msg.accessToken,
      refreshToken: msg.refreshToken,
      accessTokenExpiresAt: msg.accessTokenExpiresAt,
    });
    // A fresh token implicitly clears any soft-expired flag mirrored from a
    // transient peer state.
    useAuthStore.getState().clearSessionExpired();
  } finally {
    applyingFromBroadcast = false;
  }
}

/**
 * Install the cross-window auth bridge for the lifetime of the app shell.
 * Mount once per window (main + each pop-out). Returns a teardown.
 *
 * - Every window LISTENS for peer rotations / session-end and applies them.
 * - Only the refresh AUTHORITY (the main window) PUBLISHES: it broadcasts its
 *   own token rotations and session-end transitions. A pop-out follower never
 *   publishes (it never rotates the token), so it cannot fight the authority.
 */
export function installAuthBroadcast(
  args: CreateAuthChannelArgs = {},
): () => void {
  const channel = (args.channelFactory ?? defaultFactory)(CHANNEL_NAME);
  if (!channel) return () => undefined;

  const isAuthority = !isPopoutWindow();

  const onMessage = (event: MessageEvent): void => {
    const msg = event.data as AuthBroadcastMessage | null;
    if (!msg || typeof msg !== "object") return;
    if (msg.type === "tokens") {
      applyBroadcastTokens(msg);
      return;
    }
    // msg.type === "session": a follower mirrors the authority's session-end so
    // it can surface the inline "session ended" notice (RequireAuth) rather
    // than redirecting to /login. The authority drives its own status, so it
    // ignores an echoed session message.
    if (isAuthority) return;
    if (msg.status === "cleared") {
      useAuthStore.getState().clearSession();
    } else {
      useAuthStore.getState().markSessionExpired();
    }
  };
  channel.addEventListener("message", onMessage);

  // Publish side — authority only.
  let unsubscribe: (() => void) | undefined;
  if (isAuthority) {
    let lastAccessToken = useAuthStore.getState().session?.accessToken ?? null;
    let lastStatus = useAuthStore.getState().status;
    unsubscribe = useAuthStore.subscribe((state) => {
      // Token rotation → broadcast (unless we are mid-apply of a peer message).
      const token = state.session?.accessToken ?? null;
      if (
        !applyingFromBroadcast &&
        token !== null &&
        token !== lastAccessToken &&
        state.session
      ) {
        lastAccessToken = token;
        const post: TokensMessage = {
          type: "tokens",
          accessToken: state.session.accessToken,
          refreshToken: state.session.refreshToken,
          accessTokenExpiresAt: state.session.accessTokenExpiresAt ?? 0,
        };
        try {
          channel.postMessage(post);
        } catch {
          /* swallow */
        }
      } else if (token !== null) {
        lastAccessToken = token;
      }

      // Session-end transition → broadcast so followers stop being authed.
      if (state.status !== lastStatus) {
        const prev = lastStatus;
        lastStatus = state.status;
        if (state.status === "session_expired") {
          try {
            channel.postMessage({ type: "session", status: "expired" });
          } catch {
            /* swallow */
          }
        } else if (
          (state.status === "idle" || state.status === "wrong_tenant") &&
          prev === "authenticated"
        ) {
          // Authenticated → idle/wrong_tenant means the session was cleared.
          try {
            channel.postMessage({ type: "session", status: "cleared" });
          } catch {
            /* swallow */
          }
        }
      }
    });
  }

  return () => {
    channel.removeEventListener("message", onMessage);
    unsubscribe?.();
    try {
      channel.close();
    } catch {
      /* swallow */
    }
  };
}
